import { Announcements, ScreenReaderInstructions } from '@dnd-kit/core';
import { ITrainingBlock } from '../../../models/allenamenti';

/**
 * Istruzioni lette una volta sola da chi naviga con lo screen reader, legate
 * a ogni card trascinabile prima ancora che il drag inizi (dnd-kit le espone
 * come testo nascosto). Sostituiscono il testo di default di dnd-kit, in
 * inglese.
 */
export const WEEK_BOARD_SCREEN_READER_INSTRUCTIONS: ScreenReaderInstructions = {
  draggable:
    'Per afferrare un blocco di allenamento, premi la barra spaziatrice. ' +
    'Usa le frecce per spostarlo su un altro giorno della settimana. ' +
    'Premi di nuovo la barra spaziatrice per rilasciarlo nel giorno selezionato, oppure Esc per annullare.',
};

/**
 * Annunci letti dallo screen reader durante il trascinamento di un blocco fra
 * i giorni della griglia settimanale — sostituiscono i quattro testi di
 * default di dnd-kit (inglesi e generici, es. "Draggable item X was moved
 * over droppable area Y") con frasi che nominano il blocco e il giorno: le
 * uniche due informazioni che contano per capire cosa succede senza vedere
 * lo schermo.
 *
 * `blocks` va passato aggiornato a ogni render da chi chiama (vedi
 * WeekBoard): dnd-kit legge questi callback dal `DndContext` corrente, quindi
 * un oggetto costruito su dati vecchi userebbe titoli di blocchi non più
 * validi dopo un create/update/delete nel frattempo.
 */
export function buildWeekBoardAnnouncements(
  blocks: ITrainingBlock[],
  dayLabelByIso: Map<string, string>,
): Announcements {
  const titleFor = (id: string) => blocks.find((b) => b._id === id)?.title ?? 'il blocco';
  const dayFor = (id: string) => dayLabelByIso.get(id) ?? id;

  return {
    onDragStart({ active }) {
      const title = titleFor(String(active.id));
      return (
        `Hai afferrato il blocco "${title}". Usa le frecce per spostarlo su un altro giorno, ` +
        'la barra spaziatrice per rilasciarlo lì, Esc per annullare.'
      );
    },
    onDragOver({ active, over }) {
      const title = titleFor(String(active.id));
      if (!over) return `Il blocco "${title}" non è più sopra a nessun giorno.`;
      return `Il blocco "${title}" è sopra ${dayFor(String(over.id))}.`;
    },
    onDragEnd({ active, over }) {
      const title = titleFor(String(active.id));
      if (!over) {
        return `Blocco "${title}" rilasciato fuori da un giorno valido: la posizione non è cambiata.`;
      }
      return `Blocco "${title}" spostato su ${dayFor(String(over.id))}.`;
    },
    onDragCancel({ active }) {
      const title = titleFor(String(active.id));
      return `Spostamento annullato: il blocco "${title}" è tornato al suo giorno originale.`;
    },
  };
}
